// askSmart-ui/src/pages/TOSP/components/TenantModal.tsx
import React, { useEffect, useState } from "react";
import OccupancyCard, { UnitOption } from "./OccupancyCard";
import {
  LeasedSpaceDraft,
  OccupancyDraft,
  draftSchedule,
  draftText,
  emptyLeasedSpaceDraft,
  emptyOccupancyDraft,
  emptyRentReset,
  fromOccupancyDraft,
  moneyText,
  reviewPatch,
  toOccupancyDraft,
} from "../occupancyDraft";
import AssignUnitsStep from "./AssignUnitsStep";
import TenantInfoStep from "./TenantInfoStep";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import LeaseReviewWorkspace from "./LeaseReview/LeaseReviewWorkspace";
import { useAuth } from "../../../hooks/useAuth";
import {
  AmendmentApply,
  LeaseExtraction,
  splitReviewValues,
} from "./LeaseReview/leaseReviewFields";
import { LeaseServiceChoice } from "./LeaseReview/leaseSource";
import { readLeaseFor } from "../leaseIntake";
import TakeoverWizard, { TakeoverResult } from "./TakeoverWizard";
import TenantContactPicker from "./TenantContactPicker";
import LeaseChoiceStep from "./LeaseChoiceStep";
import TenantSalesPanel from "./TenantSalesPanel";
import LeaseSnapshot from "./LeaseSnapshot";
import ExpenseTable from "../portal/ExpenseTable";
import { useStoredSales } from "../portal/tenantSales";
import { TenantContact } from "../tenantContacts";
import { useTospData } from "../TospDataContext";
import { formatDate } from "../format";
import {
  activeOccupancyOf,
  activeOccupant,
  dayBefore,
  isOccupancyActive,
  LeaseReviewStamp,
  leasedSpaceLabel,
  Occupancy,
  tenancyEndDate,
  Tenant,
  tenantLabel,
  tenantOpeningDate,
  tenantReportsSales,
  todayISO,
  Unit,
} from "../types";
import { parseNumeric, requiredEmail, requiredText } from "../validation";

/**
 * TEMPORARY — blocks assigning a unit another tenant still actively holds.
 *
 * Taking on an occupied unit is legitimate: it's a handover, and the takeover
 * wizard exists to walk through it. But while the portfolio is being loaded
 * for the first time, every occupied unit offered is far more likely to be a
 * mis-click than a real handover, and a takeover silently ends the sitting
 * tenant's lease — quiet, plausible, and tedious to unpick.
 *
 * So an occupied unit leaves the list entirely. The list shrinks as the work
 * progresses, and an empty one is the plainest possible statement that every
 * unit is now assigned — which is also the signal that this can come out.
 *
 * Set to false to restore takeovers once the tenants are in. That single line
 * is the whole switch — both the edit form and the Review Lease assign step
 * read it.
 */
const BLOCK_OCCUPIED_UNITS = true;

export interface TenantFormValues {
  operatingName: string;
  legalEntity: string;
  retailerType: string;
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  wasteRemoval: string;
  atmRestriction: boolean;
  atmOnsite: boolean;
  depositPaidAmount: number | null;
  depositPaidDate: string;
  prepaidRentAmount: number | null;
  prepaidRentDate: string;
  /** The SMRT Docs service holding this tenancy's lease. */
  smrtDocsServiceId: string;
  occupancies: Occupancy[];
  /**
   * Outgoing leases this save ends, from takeovers agreed in the wizard.
   * Applied alongside the tenant so a handover lands as one change.
   */
  takeovers: TakeoverInstruction[];
}

export interface TakeoverInstruction {
  occupancyId: string;
  endDate: string;
}

/**
 * Everything on the form that a save would write, as one comparable string.
 *
 * Save is only worth offering when it would change something, and "something"
 * here includes the leases and any takeover agreed in the wizard — not just
 * the tenant's own boxes. Comparing a serialised snapshot rather than tracking
 * a dirty flag on every handler means a value typed and then typed back is
 * correctly no change at all.
 */
const formSnapshot = (values: {
  operatingName: string;
  legalEntity: string;
  retailerType: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  wasteRemoval: string;
  atmRestriction: boolean;
  atmOnsite: boolean;
  depositAmount: string;
  depositDate: string;
  prepaidAmount: string;
  prepaidDate: string;
  /** Included so choosing which lease this tenancy is counts as a change. */
  serviceId: string;
  occupancies: OccupancyDraft[];
  takeovers: TakeoverInstruction[];
}): string => JSON.stringify(values);

interface TenantModalProps {
  isOpen: boolean;
  /** The tenant being edited, or null when adding a new one. */
  tenant: Tenant | null;
  units: Unit[];
  /** Every tenant, so the "one active tenant per unit" rule can see the rest. */
  tenants: Tenant[];
  unitById: (unitId: string) => Unit | undefined;
  onClose: () => void;
  /** Awaited — a tenant with several occupancies is several writes. */
  onSave?: (values: TenantFormValues) => void | Promise<void>;
  /**
   * Viewing rather than editing. Same form, same layout, same expandable
   * occupancy cards — nothing changeable, and Save gives way to Close. Sharing
   * the form is what keeps the two views from drifting apart.
   */
  readOnly?: boolean;
  /**
   * A tenancy that has already ended, being recorded after the fact.
   *
   * Three things change. The lease wizard is skipped — there is no document in
   * SMRT Docs to read, because the business left before any of this existed and
   * what is wanted is the figures, not the paperwork. Units another tenant now
   * holds are OFFERED rather than hidden, because that is the whole point: the
   * unit was theirs then and is somebody else's now. And choosing one does NOT
   * start a takeover, because nothing is being handed over — the two tenancies
   * do not overlap in time, only on the unit.
   *
   * In exchange, an end date in the past is REQUIRED. Without one the record
   * would read as a live tenancy, putting two active leases on one unit, which
   * is what every "who holds this unit" question in the module relies on not
   * happening.
   */
  pastTenant?: boolean;
}

// Add / edit / view form for a single Tenant. Occupancies are edited in place
// as expandable cards beneath the tenant details — one modal, one save.
export default function TenantModal({
  isOpen,
  tenant,
  units,
  tenants,
  unitById,
  onClose,
  onSave,
  readOnly = false,
  pastTenant = false,
}: TenantModalProps) {
  // Stamped onto the review so a later reviewer knows who signed the terms off.
  // Falls back to the email — a name is nicer to read, but an address always
  // identifies somebody, and an unattributed review is the thing worth avoiding.
  const { user } = useAuth();
  const reviewerName =
    (user as { name?: string; email?: string } | null | undefined)?.name ||
    (user as { email?: string } | null | undefined)?.email ||
    "";

  // Retailer and Waste Removal types are admin-maintained under Configuration,
  // never hardcoded.
  const { configuration, unitLabel } = useTospData();
  // Read here rather than passed in: the expenses table needs the reported
  // months, and every other caller of this modal would have to thread them
  // through for a section that is usually collapsed.
  const { sales } = useStoredSales();
  const [operatingName, setOperatingName] = useState("");
  const [legalEntity, setLegalEntity] = useState("");
  const [retailerType, setRetailerType] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [wasteRemoval, setWasteRemoval] = useState("");
  const [atmRestriction, setAtmRestriction] = useState(false);
  const [atmOnsite, setAtmOnsite] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [prepaidAmount, setPrepaidAmount] = useState("");
  const [prepaidDate, setPrepaidDate] = useState("");
  const [occupancies, setOccupancies] = useState<OccupancyDraft[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  // The form's own sections, all shut until asked for. Initial state as well
  // as the reset below, or the first render would show the section open and
  // close it a frame later.
  const [infoOpen, setInfoOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  /** The occupancy whose lease is being reviewed, if any. */
  const [reviewing, setReviewing] = useState<string | null>(null);
  /**
   * Where that review has got to.
   *
   * Reviewing a lease starts the same way the wizard does — read the document,
   * find the areas it covers, match each to a unit — because that step is what
   * makes the rest meaningful, and a lease recorded before it existed has
   * never had it done. Its spaces come back unassigned and the step asks.
   */
  const [reviewStep, setReviewStep] = useState<
    "choose" | "reading" | "assign" | "review"
  >("review");
  /**
   * The leases this contact holds, when they hold more than one and this
   * tenancy hasn't been tied to one yet. Set only for as long as the question
   * is on screen — the answer goes onto the tenant record, and it isn't asked
   * again.
   */
  const [leaseChoices, setLeaseChoices] = useState<LeaseServiceChoice[] | null>(
    null,
  );
  /** The service this tenancy's lease lives on, once known. */
  const [serviceId, setServiceId] = useState<string>(
    tenant?.smrtDocsServiceId || "",
  );
  /**
   * Which step of adding a tenant is on screen.
   *
   * Adding one runs contact → lease → form, because that's the order the
   * information actually arrives in: the contact says whose lease to open, the
   * lease says who the tenant is and what they've agreed to, and the form is
   * left holding only what neither of them can answer. Editing an existing
   * tenant opens straight on the form — the first two steps are already done.
   */
  const [stage, setStage] = useState<
    "contact" | "chooseLease" | "reading" | "assign" | "info" | "review" | "form"
  >("form");
  /** The SMRT Docs contact chosen in step 1. */
  const [contact, setContact] = useState<TenantContact | null>(null);
  /** What reading the lease turned up, held until the wizard finishes. */
  const [intake, setIntake] = useState<LeaseExtraction | null>(null);
  /** Why the lease couldn't be read, if it couldn't. */
  const [intakeError, setIntakeError] = useState<string | null>(null);
  /** Unit chosen for each detected space, keyed by space id. */
  const [spaceUnits, setSpaceUnits] = useState<Record<string, string>>({});
  /** The open lease, so the review screen doesn't download it again. */
  const [leaseBytes, setLeaseBytes] = useState<Uint8Array | null>(null);
  /** The lease the wizard's review step works on. */
  const [wizardLease, setWizardLease] = useState<OccupancyDraft | null>(null);
  /** Whether the reset effect has run for this open — see the guard below. */
  const [hydrated, setHydrated] = useState(false);
  /** The form as it was when it opened, for telling whether Save has work. */
  const [baseline, setBaseline] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /** Cancel was pressed with unsaved changes on the form. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  /**
   * A lease or amendment has been verified since this form opened.
   *
   * Only changes the wording of the discard warning, but it is the difference
   * between "you typed something" and "you just read eighty pages" — and the
   * second deserves to be named, because that is the work somebody would
   * actually regret losing.
   */
  const [reviewedThisSession, setReviewedThisSession] = useState(false);
  // Takeovers agreed in the wizard, applied when the tenant is saved so a
  // cancelled edit never ends someone else's lease.
  const [takeovers, setTakeovers] = useState<TakeoverInstruction[]>([]);
  // The handover being walked through, if any.
  const [takeover, setTakeover] = useState<{
    /**
     * Where the assignment lands once the handover is agreed: a leased space
     * already on the form, or one the Assign Units step is still choosing for.
     * The two hold their units in different places.
     */
    target: "form" | "wizard";
    /** The incoming lease's space that the unit is being assigned to. */
    spaceId: string;
    unitId: string;
    unitLabel: string;
    tenantName: string;
    occupancyId: string;
    /** The outgoing lease's other spaces, which end with it. */
    alsoEnding: string[];
  } | null>(null);

  // Reload the form whenever it opens, so a cancelled edit leaves nothing
  // behind for the next Add.
  useEffect(() => {
    if (!isOpen) return;
    // The deposit used to be the tenant's. It's a lease term, so it has moved
    // onto the lease — and where a tenant holds exactly one, that lease is
    // unambiguously the one the money was for, so it's carried across.
    //
    // A tenant with several leases is left alone: which of them the deposit
    // was paid under isn't knowable from the record, and putting it on the
    // wrong one would be inventing a fact rather than reading one forward.
    const drafts = (tenant?.occupancies || []).map(toOccupancyDraft);
    const soleLease = drafts.length === 1 ? drafts[0] : null;
    if (
      soleLease &&
      !String(soleLease.securityDepositAmount || "").trim() &&
      tenant?.depositPaidAmount !== null &&
      tenant?.depositPaidAmount !== undefined
    ) {
      soleLease.securityDepositAmount = String(tenant.depositPaidAmount);
      soleLease.depositPaidDate = tenant.depositPaidDate || "";
    }
    // Built as one object so the same values that seed the form are the ones
    // remembered as its starting point — a baseline assembled separately would
    // drift the moment either side changed.
    const opening = {
      operatingName: tenant?.operatingName || "",
      legalEntity: tenant?.legalEntity || "",
      retailerType: tenant?.retailerType || "",
      contactName: tenant?.primaryContactName || "",
      contactPhone: tenant?.primaryContactPhone || "",
      contactEmail: tenant?.primaryContactEmail || "",
      wasteRemoval: tenant?.wasteRemoval || "",
      atmRestriction: tenant?.atmRestriction ?? false,
      atmOnsite: tenant?.atmOnsite ?? false,
      depositAmount: moneyText(tenant?.depositPaidAmount),
      depositDate: tenant?.depositPaidDate || "",
      prepaidAmount: moneyText(tenant?.prepaidRentAmount),
      prepaidDate: tenant?.prepaidRentDate || "",
      serviceId: tenant?.smrtDocsServiceId || "",
      occupancies: drafts,
      takeovers: [] as TakeoverInstruction[],
    };

    setOperatingName(opening.operatingName);
    setLegalEntity(opening.legalEntity);
    setRetailerType(opening.retailerType);
    setContactName(opening.contactName);
    setContactPhone(opening.contactPhone);
    setContactEmail(opening.contactEmail);
    setWasteRemoval(opening.wasteRemoval);
    setAtmRestriction(opening.atmRestriction);
    setAtmOnsite(opening.atmOnsite);
    setDepositAmount(opening.depositAmount);
    setDepositDate(opening.depositDate);
    setPrepaidAmount(opening.prepaidAmount);
    setPrepaidDate(opening.prepaidDate);
    setServiceId(opening.serviceId);
    setLeaseChoices(null);
    setOccupancies(drafts);
    setBaseline(formSnapshot(opening));
    // Both belong to the last time this form was open, not to this one.
    setConfirmDiscard(false);
    setReviewedThisSession(false);
    // Existing occupancies start collapsed — the tenant details are what the
    // admin came for; a new card opens expanded.
    setExpanded([]);
    // Everything collapsed, including the tenant's own details. The form opens
    // as a list of what's on the record, so the admin chooses what to work on
    // rather than scrolling past a section they didn't come for.
    setInfoOpen(false);
    setSalesOpen(false);
    setReviewing(null);
    setReviewStep("review");
    // Adding starts at step 1; editing and viewing open on the form, which is
    // what they came for.
    setStage(tenant || readOnly ? "form" : "contact");
    setContact(null);
    setIntake(null);
    setIntakeError(null);
    setSpaceUnits({});
    setLeaseBytes(null);
    setWizardLease(null);
    setErrors({});
    setBusy(false);
    setTakeovers([]);
    setTakeover(null);
    setHydrated(true);
  }, [isOpen, tenant, readOnly, pastTenant]);

  // Closing clears it, so the next open waits for its own reset.
  useEffect(() => {
    if (!isOpen) setHydrated(false);
  }, [isOpen]);

  // Nothing renders until the reset above has run for this open.
  //
  // The modal stays mounted between opens, so its state is still the last
  // tenant's on the first render of the next one — which for the add flow
  // means a frame of the filled-in form before the contact step replaces it.
  // A frame of nothing is the honest version of "opening".
  if (!isOpen || !hydrated) return null;

  // Declared ahead of the early returns below, not with the other handlers —
  // the lease review returns before them, so a handler defined further down is
  // still in its temporal dead zone when the review's Finish fires.
  //
  // The result is asserted back to the draft type: spreading a Partial widens
  // every property with `undefined`, which the draft's index signature doesn't
  // admit, even though a patch never actually carries one.
  const updateOccupancy = (id: string, patch: Partial<OccupancyDraft>) =>
    setOccupancies((prev) =>
      prev.map((o) => (o.id === id ? ({ ...o, ...patch } as OccupancyDraft) : o)),
    );

  // Applies an amendment (LAA) onto an occupancy draft. The new rent lands as a
  // rent reset — the same mechanism a manually-added renewal uses, so the budget
  // maths picks it up unchanged — one extension is marked used, the notes are
  // appended, and the review stamp re-points to the amendment. Nothing the
  // amendment doesn't touch is disturbed, and every existing control still works
  // by hand afterwards.
  const applyAmendment = (
    draft: OccupancyDraft,
    amendment: AmendmentApply,
    review: LeaseReviewStamp,
  ): Partial<OccupancyDraft> => {
    // Re-confirming an amendment already applied corrects the renewal it made;
    // a new one appends. Told apart by the reset id the review hands back —
    // without it, confirming the same amendment twice left the lease carrying
    // two identical rent commencements.
    const reconfirming = Boolean(
      amendment.resetId &&
        draft.leasedSpaces.some((space) =>
          space.rentResets.some((reset) => reset.id === amendment.resetId),
        ),
    );

    const leasedSpaces = draft.leasedSpaces.map((space) => {
      if (space.id !== amendment.spaceId) return space;

      const applied = {
        rentCommenceDate: amendment.rentCommenceDate,
        rentPsfCommencement: amendment.rentPsfCommencement,
        // Read off the amendment, so the form won't offer to delete it — the
        // renewal is a term of a signed document, not a row an admin added and
        // can take back. Stamped on a re-confirm too, which is how a renewal
        // made before this mark existed comes to carry it.
        fromAmendment: true,
      };

      return {
        ...space,
        rentResets: reconfirming
          ? space.rentResets.map((reset) =>
              reset.id === amendment.resetId ? { ...reset, ...applied } : reset,
            )
          : [...space.rentResets, { ...emptyRentReset(), ...applied }],
      };
    });

    const termCount = draft.extensionTerms.filter((t) => t.trim()).length;
    const extensionTermsUsed = reconfirming
      ? // Already counted when this amendment was first applied. Counting it
        // again on every re-read would work through the lease's extensions
        // without a single further renewal being agreed.
        draft.extensionTermsUsed ?? 0
      : amendment.markExtensionUsed
        ? Math.min((draft.extensionTermsUsed ?? 0) + 1, termCount)
        : draft.extensionTermsUsed ?? 0;

    // Appended, not replaced, so a second amendment doesn't erase the first's
    // notes — and skipped when the same text is already there, so re-reviewing
    // the same amendment doesn't duplicate it.
    const existingNotes = draftText(draft, "extensionNotes").trim();
    const extensionNotes =
      amendment.extensionNotes && !existingNotes.includes(amendment.extensionNotes)
        ? [existingNotes, amendment.extensionNotes].filter(Boolean).join("\n\n")
        : existingNotes || amendment.extensionNotes;

    return {
      leasedSpaces,
      extensionTermsUsed,
      extensionNotes,
      leaseReview: review,
    } as Partial<OccupancyDraft>;
  };

  /** Patches one leased space, found by id across every lease on the form. */
  const updateSpace = (spaceId: string, patch: Partial<LeasedSpaceDraft>) =>
    setOccupancies((prev) =>
      prev.map((occupancy) => {
        if (!occupancy.leasedSpaces.some((space) => space.id === spaceId)) return occupancy;
        return {
          ...occupancy,
          leasedSpaces: occupancy.leasedSpaces.map((space) =>
            space.id === spaceId ? { ...space, ...patch } : space,
          ),
        } as OccupancyDraft;
      }),
    );

  /**
   * The lease's areas as leased spaces: what the document says, carrying the
   * units just chosen.
   *
   * An area whose name matches one already on the record keeps that record's
   * space — its id, and the rent renewals recorded against it, which the
   * review never asks about. Anything else is a space the lease turned out to
   * have that the record didn't.
   */
  const buildLeasedSpaces = (
    values: Record<string, string>,
    existing: LeasedSpaceDraft[],
  ): LeasedSpaceDraft[] => {
    const { occupancy, spaces: spaceValues } = splitReviewValues(values);
    const byName = new Map(
      existing.map((space) => [space.name.trim().toLowerCase(), space]),
    );

    return (intake?.spaces || []).map((space, index) => {
      const base =
        byName.get(space.name.trim().toLowerCase()) ?? emptyLeasedSpaceDraft(space.name);
      return {
        ...base,
        name: space.name,
        unitId: spaceUnits[space.id] || "",
        rentCommenceDate: occupancy.rentCommenceDate || "",
        // The lease's own rows are the main commercial area's, so the first
        // space takes its rate from those and every other from its own box.
        rentPsfCommencement:
          index === 0
            ? occupancy.rentPsfCommencement || ""
            : spaceValues[space.id]?.rentPsfCommencement || "",
        minimumIncrease: occupancy.minimumIncrease || "",
        maximumIncrease: occupancy.maximumIncrease || "",
        rentStepUpMonth: occupancy.rentStepUpMonth || "",
      } as LeasedSpaceDraft;
    });
  };

  // Areas on the assign step, edited by hand.
  //
  // The list starts from what the lease yielded, but lease forms differ by
  // landlord and the rules can only recognise the shapes they've met. One area
  // too few left the reviewer on a step with nothing to assign; one too many
  // left them with no way to drop it. Both are now corrections rather than
  // dead ends, and the correction is made where the problem is visible.
  const addDetectedSpace = () =>
    setIntake((prev) =>
      prev
        ? {
            ...prev,
            spaces: [
              ...prev.spaces,
              {
                id: `man_${Date.now()}_${prev.spaces.length}`,
                name: "",
                values: {},
                added: true,
              },
            ],
          }
        : prev,
    );

  const removeDetectedSpace = (spaceId: string) => {
    setIntake((prev) =>
      prev
        ? { ...prev, spaces: prev.spaces.filter((space) => space.id !== spaceId) }
        : prev,
    );
    // The unit picked for it goes too, or a removed area would keep holding a
    // unit that nothing on screen accounts for.
    setSpaceUnits((prev) => {
      const next = { ...prev };
      delete next[spaceId];
      return next;
    });
  };

  const renameDetectedSpace = (spaceId: string, name: string) =>
    setIntake((prev) =>
      prev
        ? {
            ...prev,
            spaces: prev.spaces.map((space) =>
              space.id === spaceId ? { ...space, name } : space,
            ),
          }
        : prev,
    );

  /**
   * Reads a lease during Add Tenant.
   *
   * Shared by the contact step and the lease chooser, so the two can't drift
   * on what happens after the read. A contact holding several tenancies is
   * asked which lease this one is; with one, nothing is asked.
   */
  const readForWizard = async (email: string, useServiceId: string) => {
    const result = await readLeaseFor(email, useServiceId);

    if (result.status === "missing") {
      setIntakeError(result.reason);
      return;
    }
    if (result.status === "choose") {
      setLeaseChoices(result.services);
      setStage("chooseLease");
      return;
    }

    // Recorded from what the lookup actually resolved, not from what was
    // asked. A contact with one lease is never asked, and that tenancy still
    // needs to know where its lease lives.
    if (result.serviceId) setServiceId(result.serviceId);
    setIntake(result.extraction);
    setLeaseBytes(result.bytes);
    setStage("assign");
  };

  /** Reads the lease for an existing tenant, then asks for its units. */
  const startReview = async (occupancyId: string, useServiceId?: string) => {
    setReviewing(occupancyId);
    setReviewStep("reading");
    setIntakeError(null);
    setSpaceUnits({});

    const chosen = useServiceId ?? serviceId;
    const result = await readLeaseFor(contactEmail, chosen);

    if (result.status === "missing") {
      setIntakeError(result.reason);
      return;
    }

    // This contact holds more than one tenancy and nothing yet says which
    // lease is this one's. Asked here, once — the answer is saved with the
    // tenant and every later review reads it straight through.
    if (result.status === "choose") {
      setLeaseChoices(result.services);
      setReviewStep("choose");
      return;
    }

    // As in the wizard: taken from what was resolved, so a single-lease
    // tenancy records its service without ever being asked.
    if (result.serviceId) setServiceId(result.serviceId);
    setIntake(result.extraction);
    setLeaseBytes(result.bytes);

    // A re-review is an amendment: the occupancy's areas and the units they sit
    // on were settled in the original review, and an amendment doesn't re-demise
    // the premises. So once an occupancy has been verified once, the assign step
    // is skipped and the review opens straight onto the workspace, which keeps
    // the verified terms and asks only for the amendment's changes. Only a first
    // review (no stamp yet) walks the assign step.
    const occupancy = occupancies.find((o) => o.id === occupancyId);
    if (occupancy?.leaseReview) {
      setReviewStep("review");
      return;
    }

    // Units already assigned to an area of the same name carry over, so a
    // lease reviewed twice isn't re-assigned from scratch. A record made
    // before this step existed has no matching names and nothing carries —
    // which is exactly the case that needs asking about.
    const held = occupancy?.leasedSpaces || [];
    const byName = new Map(
      held.map((space) => [space.name.trim().toLowerCase(), space.unitId]),
    );
    const prefill: Record<string, string> = {};
    for (const space of result.extraction.spaces) {
      const unitId = byName.get(space.name.trim().toLowerCase());
      if (unitId) prefill[space.id] = unitId;
    }
    setSpaceUnits(prefill);
    setReviewStep("assign");
  };

  /**
   * Who holds a unit now, and from when — what bounds a past tenancy on it.
   *
   * The successor's commencement is the answer to "when did the tenancy before
   * it end": the day before. That is the convention the takeover wizard already
   * writes (outgoing ends the day BEFORE the takeover date), and it matters
   * rather than being tidiness — the recovery split counts days, so two leases
   * sharing a single day on one unit would bill that unit's tax twice.
   *
   * The LEASE's commencement, not the space's rent commencement. The two differ
   * whenever there is a fixturing period: the tenant takes the unit on the
   * takeover date and starts paying weeks later. What ends the tenancy before
   * them is possession, not rent — and the takeover wizard writes the takeover
   * date onto exactly this field. The space's own date is the fallback for a
   * record that never had a lease commencement entered.
   *
   * Deliberately the other way round from the recovery split in revenueBudget,
   * which asks a different question — when does this lease start being CHARGED
   * on this unit — and rightly prefers the space.
   */
  const successorOn = (unitId: string): { name: string; since: string } | null => {
    const held = activeOccupancyOf(unitId, tenants, tenant?.id);
    if (!held) return null;
    const since = held.occupancy.leaseCommenceDate || held.space.rentCommenceDate || "";
    return { name: tenantLabel(held.tenant), since };
  };

  // Units offered in the wizard, labelled "[Building]-[Suite]".
  const allUnitOptions: UnitOption[] = units
    // Same as the edit form: while loading the portfolio, units another tenant
    // holds are gone from the list rather than greyed.
    // A PAST tenancy keeps them all. The unit it held is, by definition, one
    // somebody else holds now, so hiding those would leave nothing to assign.
    .filter(
      (unit) =>
        pastTenant ||
        !BLOCK_OCCUPIED_UNITS ||
        !activeOccupant(unit.id, tenants, tenant?.id),
    )
    .map((unit) => {
      const other = activeOccupant(unit.id, tenants, tenant?.id);
      // On a past tenancy the occupant's start date is the useful part: it is
      // what the lease being recorded has to end before. Shown on the option
      // itself, so it is read while the unit is being chosen rather than
      // afterwards in an error.
      const since = pastTenant ? successorOn(unit.id)?.since : "";
      return {
        unit,
        label: unitLabel(unit),
        occupiedBy: other
          ? since
            ? `${tenantLabel(other)} since ${formatDate(since)}`
            : tenantLabel(other)
          : undefined,
      };
    });

  /**
   * A unit chosen in the Assign Units step.
   *
   * Choosing one somebody else still holds is a handover, not a clash, so the
   * wizard runs before the assignment sticks — the same rule the occupancy
   * form has always applied, which the Add Tenant wizard was missing.
   */

  const chooseUnit = (spaceId: string, unitId: string) => {
    // A past tenancy assigns the unit and nothing else happens: it takes
    // nothing over, because it ended before the current tenant arrived. The
    // two share the unit only on paper, not in time.
    if (unitId && !pastTenant) {
      const held = activeOccupancyOf(unitId, tenants, tenant?.id);
      const unit = unitById(unitId);
      // The greyed option can't be clicked, but a select can still be driven
      // by keyboard or autofill — and this one ends somebody's lease, so it
      // is refused here as well as disabled there.
      if (held && BLOCK_OCCUPIED_UNITS) return;
      if (held && unit) {
        setTakeover({
          target: "wizard",
          spaceId,
          unitId,
          unitLabel: unitLabel(unit),
          tenantName: tenantLabel(held.tenant),
          occupancyId: held.occupancy.id,
          alsoEnding: (held.occupancy.leasedSpaces || [])
            .filter((other) => other.id !== held.space.id)
            .map((other) =>
              leasedSpaceLabel(other, other.unitId ? unitById(other.unitId) : undefined),
            ),
        });
        return;
      }
    }

    // A past tenancy on an occupied unit gets its end date from the successor:
    // the day before they commenced. Filled in rather than asked for, because
    // the answer is already on the record and typing it again is a chance to
    // get it wrong. Only when blank — a date entered by hand stands.
    if (pastTenant && unitId && wizardLease && !wizardLease.leaseEndDate) {
      const next = successorOn(unitId);
      if (next?.since) {
        setWizardLease((lease) =>
          lease ? { ...lease, leaseEndDate: dayBefore(next.since) } : lease,
        );
      }
    }

    setSpaceUnits((prev) => ({ ...prev, [spaceId]: unitId }));
  };

  // The unit is assigned only once the handover is agreed, with the incoming
  // lease starting on the date chosen and the outgoing one queued to end.
  const applyTakeover = (result: TakeoverResult) => {
    if (!takeover) return;

    if (takeover.target === "wizard") {
      // The incoming lease's own commencement date isn't set here — it comes
      // off the document in the review step, which is the more reliable
      // source. Only the assignment and the outgoing end date are agreed now.
      setSpaceUnits((prev) => ({ ...prev, [takeover.spaceId]: takeover.unitId }));
      setTakeovers((prev) => [
        ...prev.filter((t) => t.occupancyId !== result.outgoingOccupancyId),
        { occupancyId: result.outgoingOccupancyId, endDate: result.outgoingEndDate },
      ]);
      setTakeover(null);
      return;
    }

    updateSpace(takeover.spaceId, { unitId: takeover.unitId });
    // The commencement date is the lease's, so it lands on the lease holding
    // the space rather than on the space itself.
    const owner = occupancies.find((o) =>
      o.leasedSpaces.some((space) => space.id === takeover.spaceId),
    );
    if (owner) {
      updateOccupancy(owner.id, { leaseCommenceDate: result.newCommenceDate });
    }
    setTakeovers((prev) => [
      ...prev.filter((t) => t.occupancyId !== result.outgoingOccupancyId),
      { occupancyId: result.outgoingOccupancyId, endDate: result.outgoingEndDate },
    ]);
    setTakeover(null);
  };

  // Rendered beside every screen that can assign a unit, because each of them
  // returns before the form does and the wizard would otherwise never appear.
  const takeoverWizard = (
    <TakeoverWizard
      isOpen={!!takeover}
      outgoingTenantName={takeover?.tenantName || ""}
      outgoingOccupancyId={takeover?.occupancyId || ""}
      incomingTenantName={operatingName || legalEntity}
      unitLabel={takeover?.unitLabel || ""}
      alsoEnding={takeover?.alsoEnding || []}
      onCancel={() => setTakeover(null)}
      onConfirm={applyTakeover}
    />
  );

  // ── Add Tenant: contact → read the lease → assign units → tenant info ─────

  if (stage === "contact") {
    // Reads the lease straight away. Their details come off the SMRT Docs
    // record, and the email is what the lease is found by.
    const next = async (picked: TenantContact) => {
      setContact(picked);
      setContactName(picked.name);
      setContactEmail(picked.email);
      setContactPhone(picked.phone);
      setStage("reading");
      setIntakeError(null);

      await readForWizard(picked.email, "");
    };

    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal">
          <TenantContactPicker onClose={onClose} onNext={next} />
        </div>
      </div>
    );
  }

  if (stage === "chooseLease" && leaseChoices) {
    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal">
          <LeaseChoiceStep
            services={leaseChoices}
            chosen={serviceId}
            onChoose={setServiceId}
            onClose={onClose}
            onBack={() => {
              setLeaseChoices(null);
              setServiceId("");
              setStage("contact");
            }}
            onNext={async () => {
              setLeaseChoices(null);
              setStage("reading");
              setIntakeError(null);
              await readForWizard(contactEmail, serviceId);
            }}
          />
        </div>
      </div>
    );
  }

  if (stage === "reading") {
    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal">
          <div className="smrtdocs-modal-header tosp-wizard-header">
            <h2 style={{ margin: 0, fontSize: "1rem" }}>Add Tenant</h2>
            <button
              type="button"
              className="tosp-wizard-close"
              aria-label="Cancel"
              title="Cancel"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div className="smrtdocs-modal-body tosp-wizard-reading">
            {intakeError ? (
              <>
                <p className="tosp-field-error">{intakeError}</p>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setStage("contact")}
                >
                  Back
                </button>
              </>
            ) : (
              <>
                <span className="tosp-spinner tosp-spinner-lg" aria-hidden="true" />
                <p>Reviewing the lease…</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (stage === "assign") {
    const spaces = (intake?.spaces || []).map((space) => ({
      id: space.id,
      name: space.name,
        added: space.added === true,
    }));

    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal">
          <AssignUnitsStep
            spaces={spaces}
            unitOptions={allUnitOptions}
            chosen={spaceUnits}
            onChoose={chooseUnit}
            onAddSpace={addDetectedSpace}
            onRemoveSpace={removeDetectedSpace}
            onRenameSpace={renameDetectedSpace}
            onBack={() => setStage("contact")}
            onClose={onClose}
            onNext={() => setStage("info")}
          />
        </div>
        {takeoverWizard}
      </div>
    );
  }

  if (stage === "info") {
    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal">
          <TenantInfoStep
            retailerTypes={configuration.retailerTypes}
            wasteRemovalTypes={configuration.wasteRemovalTypes}
            onBack={() => setStage("assign")}
            onClose={onClose}
            onNext={(values) => {
              setRetailerType(values.retailerType);
              setWasteRemoval(values.wasteRemoval);
              // A lease for the review to fill in. Empty, so every term the
              // extractor finds arrives as a suggestion rather than as a
              // disagreement with something nobody entered.
              setWizardLease(emptyOccupancyDraft());
              setStage("review");
            }}
          />
        </div>
      </div>
    );
  }

  // Step 4: the lease's terms, verified against the document itself.
  //
  // The same screen the Review Lease button opens, on the lease the wizard is
  // building. It's handed the bytes step 2 already downloaded, so the document
  // isn't fetched and parsed a second time.
  if (stage === "review" && wizardLease) {
    return (
      <div className="smrtdocs-modal-overlay">
        <div className="smrtdocs-modal tosp-modal-review">
          <LeaseReviewWorkspace
            tenant={
              tenant ?? (contact ? ({ primaryContactEmail: contact.email } as Tenant) : null)
            }
            draft={wizardLease}
            tenantValues={{ operatingName, legalEntity }}
            reviewerName={reviewerName}
            initialBytes={leaseBytes}
            onCancel={() => setStage("info")}
            // Verify ends the wizard. What it collected is assembled into one
            // lease and put on the form, where the admin can still change
            // anything — nothing is written to the record until they save.
            onFinish={(values, review) => {
              setReviewedThisSession(true);
              const { tenant: onTenant, occupancy } = splitReviewValues(values);
              if (onTenant.operatingName) setOperatingName(onTenant.operatingName);
              if (onTenant.legalEntity) setLegalEntity(onTenant.legalEntity);
              if (occupancy.securityDepositAmount) {
                setDepositAmount(occupancy.securityDepositAmount);
              }

              // Nothing on the record yet to carry forward — this tenant is
              // being created — so the areas are built from the document alone.
              const lease = {
                ...emptyOccupancyDraft(),
                ...reviewPatch(occupancy),
                leasedSpaces: buildLeasedSpaces(values, []),
                leaseReview: review,
              } as OccupancyDraft;

              setOccupancies([lease]);
              setExpanded([lease.id]);
              setInfoOpen(true);
              setStage("form");
            }}
          />
        </div>
      </div>
    );
  }

  // The lease review takes the whole modal while it's open — the admin is
  // reading a document, and a side-by-side workspace can't share the width
  // with the form it's going to fill in.
  if (reviewing) {
    const draft = occupancies.find((o) => o.id === reviewing);

    // A contact with two tenancies is asked which lease this one is, before
    // anything is read. Only reached while the tenancy has no service recorded
    // — once it has, every later review goes straight to reading.
    if (draft && reviewStep === "choose" && leaseChoices) {
      return (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal">
            <LeaseChoiceStep
              services={leaseChoices}
              chosen={serviceId}
              onChoose={setServiceId}
              onClose={() => {
                setLeaseChoices(null);
                setReviewing(null);
                setReviewStep("review");
              }}
              onBack={() => {
                setLeaseChoices(null);
                setReviewing(null);
                setReviewStep("review");
              }}
              onNext={async () => {
                setLeaseChoices(null);
                await startReview(reviewing, serviceId);
              }}
            />
          </div>
        </div>
      );
    }

    // Same two steps the wizard opens with: read the document, then match the
    // areas it covers to units.
    if (draft && reviewStep === "reading") {
      return (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal">
            <div className="smrtdocs-modal-header tosp-wizard-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Review Lease</h2>
              <button
                type="button"
                className="tosp-wizard-close"
                aria-label="Cancel"
                title="Cancel"
                onClick={() => setReviewing(null)}
              >
                ×
              </button>
            </div>
            <div className="smrtdocs-modal-body tosp-wizard-reading">
              {intakeError ? (
                <>
                  <p className="tosp-field-error">{intakeError}</p>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setReviewing(null)}
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <span className="tosp-spinner tosp-spinner-lg" aria-hidden="true" />
                  <p>Reviewing the lease…</p>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (draft && reviewStep === "assign") {
      return (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal">
            <AssignUnitsStep
              spaces={(intake?.spaces || []).map((space) => ({
                id: space.id,
                name: space.name,
        added: space.added === true,
              }))}
              unitOptions={allUnitOptions}
              chosen={spaceUnits}
              onChoose={chooseUnit}
              onAddSpace={addDetectedSpace}
              onRemoveSpace={removeDetectedSpace}
              onRenameSpace={renameDetectedSpace}
              onBack={() => setReviewing(null)}
              onClose={() => setReviewing(null)}
              onNext={() => setReviewStep("review")}
            />
          </div>
          {takeoverWizard}
        </div>
      );
    }

    if (draft) {
      return (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal tosp-modal-review">
            <LeaseReviewWorkspace
              tenant={tenant}
              draft={draft}
              tenantValues={{ operatingName, legalEntity }}
            reviewerName={reviewerName}
              onCancel={() => setReviewing(null)}
              onFinish={(values, review, amendment) => {
                // An amendment (LAA) keeps the verified terms as they are and
                // lands only its own changes — the new rent as a rent reset, the
                // notes, and one extension marked used.
                setReviewedThisSession(true);
                if (amendment) {
                  updateOccupancy(reviewing, applyAmendment(draft, amendment, review));
                  setReviewing(null);
                  setExpanded((prev) => [...new Set([...prev, reviewing])]);
                  return;
                }

                // Straight onto the form, where the admin can still change
                // anything before saving — Finish ends the review, it doesn't
                // commit the record.
                //
                // The lease names the tenant as well as its terms, and those
                // live on different records, so the reviewed values are sorted
                // before being applied.
                const { tenant: onTenant, occupancy } = splitReviewValues(values);
                if (onTenant.operatingName !== undefined) {
                  setOperatingName(onTenant.operatingName);
                }
                if (onTenant.legalEntity !== undefined) {
                  setLegalEntity(onTenant.legalEntity);
                }
                if (occupancy.securityDepositAmount !== undefined) {
                  setDepositAmount(occupancy.securityDepositAmount);
                }

                // The stamp rides with the values: what was signed off is only
                // meaningful alongside the figures it was signed off against.
                updateOccupancy(reviewing, {
                  ...reviewPatch(occupancy),
                  leasedSpaces: buildLeasedSpaces(values, draft.leasedSpaces),
                  leaseReview: review,
                });
                setReviewing(null);
                setExpanded((prev) => [...new Set([...prev, reviewing])]);
              }}
            />
          </div>
        </div>
      );
    }
  }

  // Choosing a unit somebody else holds isn't an error — it's a handover, so
  // the wizard runs before the unit is assigned. Anything else applies
  // straight away.
  //
  // A takeover ends the outgoing lease, not just the space being handed over:
  // a lease is a single agreement and its term runs out as one. So the wizard
  // is told every space that lease demises, and the admin can see they are
  // ending four of them to take on one.
  const changeSpace = (spaceId: string, patch: Partial<LeasedSpaceDraft>) => {
    const chosenUnitId = typeof patch.unitId === "string" ? patch.unitId : "";
    // A past tenancy takes nothing over: the unit was theirs then and is
    // somebody else's now, and the sitting tenant's lease must not be touched.
    if (chosenUnitId && !pastTenant) {
      const held = activeOccupancyOf(chosenUnitId, tenants, tenant?.id);
      const unit = unitById(chosenUnitId);
      if (held && unit) {
        setTakeover({
          target: "form",
          spaceId,
          unitId: chosenUnitId,
          unitLabel: unitLabel(unit),
          tenantName: tenantLabel(held.tenant),
          occupancyId: held.occupancy.id,
          alsoEnding: (held.occupancy.leasedSpaces || [])
            .filter((other) => other.id !== held.space.id)
            .map((other) =>
              leasedSpaceLabel(
                other,
                other.unitId ? unitById(other.unitId) : undefined,
              ),
            ),
        });
        return;
      }
    }

    // Same rule on the form as in the wizard: the successor's commencement
    // dates the end of the tenancy before it.
    if (pastTenant && chosenUnitId) {
      const next = successorOn(chosenUnitId);
      if (next?.since) {
        setOccupancies((prev) =>
          prev.map((occupancy) =>
            occupancy.leasedSpaces.some((space) => space.id === spaceId) &&
            !occupancy.leaseEndDate
              ? { ...occupancy, leaseEndDate: dayBefore(next.since) }
              : occupancy,
          ),
        );
      }
    }

    updateSpace(spaceId, patch);
  };

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Units offered on each leased space, labelled "[Building]-[Suite]". A unit
  // this tenant already holds on a different space is shown but not
  // selectable. One ANOTHER tenant holds is normally selectable — that's a
  // takeover — but is filtered out entirely while BLOCK_OCCUPIED_UNITS is on.
  const unitOptionsFor = (current: LeasedSpaceDraft): UnitOption[] =>
    units
      // While the portfolio is being loaded, a unit another tenant already
      // holds leaves the list entirely rather than sitting greyed. The list
      // shrinks as the work progresses, and an empty one is the plainest
      // possible statement that every unit is now assigned.
      //
      // The unit this space already has always stays, or the select would
      // have no option matching its own value and read as unassigned.
      //
      // A PAST tenancy keeps them all. The unit it needs is, by definition,
      // one somebody else holds now — hiding those would leave nothing to
      // assign, which is the reason this form exists.
      .filter(
        (unit) =>
          pastTenant ||
          !BLOCK_OCCUPIED_UNITS ||
          unit.id === current.unitId ||
          !activeOccupant(unit.id, tenants, tenant?.id),
      )
      .map((unit) => {
      const alreadyChosen = unit.id === current.unitId;
      const otherTenant = activeOccupant(unit.id, tenants, tenant?.id);
      const heldHere = occupancies.some(
        (occupancy) =>
          isOccupancyActive(draftSchedule(occupancy)) &&
          occupancy.leasedSpaces.some(
            (space) => space.id !== current.id && space.unitId === unit.id,
          ),
      );
      return {
        unit,
        label: unitLabel(unit),
        // Held by someone else: selectable, and doing so starts a takeover.
        // Unreachable while BLOCK_OCCUPIED_UNITS is on — those units are
        // filtered out above.
        occupiedBy: alreadyChosen || !otherTenant ? undefined : tenantLabel(otherTenant),
        // Held by this same tenant elsewhere: there's nothing to take over.
        // Left greyed rather than removed — she may want to move the unit from
        // one space to another, and it has to be visible to be moved.
        disabled: !alreadyChosen && !otherTenant && heldHere,
      };
    });

  // A lease's own messages plus its spaces', which the card sorts out by the
  // "space_<id>_" prefix.
  const errorsFor = (id: string): Record<string, string> => {
    const prefix = `occ_${id}_`;
    return Object.entries(errors)
      .filter(([key]) => key.startsWith(prefix))
      .reduce((acc, [key, value]) => ({ ...acc, [key.slice(prefix.length)]: value }), {});
  };

  const validate = (): { values?: TenantFormValues; errors: Record<string, string> } => {
    const next: Record<string, string> = {};

    // The operating name identifies the tenant, so it's required. The legal
    // entity often isn't known until the lease is to hand — blocking the
    // record on it would mean not recording a tenant who plainly exists.
    const nameError = requiredText(operatingName, "Operating Name");
    if (nameError) next.operatingName = nameError;
    if (!retailerType) next.retailerType = "Retailer Type is required.";

    const contactNameError = requiredText(contactName, "Primary Contact Name");
    if (contactNameError) next.contactName = contactNameError;

    const phoneError = requiredText(contactPhone, "Primary Contact Phone");
    if (phoneError) next.contactPhone = phoneError;

    const emailError = requiredEmail(contactEmail, "Primary Contact Email");
    if (emailError) next.contactEmail = emailError;

    if (!wasteRemoval) next.wasteRemoval = "Waste Removal is required.";

    // The deposit is no longer typed on this form — it's a lease term now — so
    // there is nothing here to validate. A legacy value that never parsed is
    // carried through untouched rather than blocking a save behind a field the
    // admin can no longer see.

    if (occupancies.length === 0) next.occupancies = "At least one lease is required.";

    occupancies.forEach((occupancy) => {
      const prefix = `occ_${occupancy.id}_`;
      if (occupancy.leasedSpaces.length === 0) {
        next[`${prefix}leasedSpaces`] = "A lease must demise at least one space.";
      }

      // A past tenancy MUST have ended, and must have ended already.
      //
      // This is what keeps it out of the way of the tenant who holds the unit
      // now. Every "who is in this unit" question in the module — the Units
      // tab, the takeover guard, the tax and BMVA recoveries — reads a lease
      // with no end date as live, so one saved without a date here would put
      // two active tenancies on one unit and be billed twice. It would also
      // never appear on the Past Tenants tab it was entered from.
      if (pastTenant) {
        const ended = occupancy.leaseEndDate;
        if (!ended) {
          next[`${prefix}leaseEndDate`] =
            "A past tenancy needs the date its lease ended.";
        } else if (ended >= todayISO()) {
          next[`${prefix}leaseEndDate`] =
            "That date hasn't passed yet, so this would still be a current tenant.";
        } else {
          // And it must have ended before whoever holds the unit now moved in.
          // Overlapping by even a day would have both leases running on that
          // unit, and the recovery split counts days — so its tax and BMVA
          // would be billed twice for the overlap.
          for (const space of occupancy.leasedSpaces) {
            if (!space.unitId) continue;
            const after = successorOn(space.unitId);
            if (after?.since && ended >= after.since) {
              next[`${prefix}leaseEndDate`] =
                `${after.name} has held that unit since ${formatDate(after.since)}, so this lease ` +
                `must end on or before ${formatDate(dayBefore(after.since))}.`;
              break;
            }
          }
        }
      }

      occupancy.leasedSpaces.forEach((space) => {
        const key = (field: string) => `${prefix}space_${space.id}_${field}`;
        const rent = space.rentPsfCommencement.trim();

        // A space has to be identifiable. Without a name and without a unit
        // there is nothing on the row at all.
        if (!space.name.trim() && !space.unitId) {
          next[key("name")] = "Name the space, or assign it a unit.";
        }

        // Rent is charged on the unit's area, so a space that carries rent
        // has to point at one. A space with no rent needs no unit — a terrace
        // that generates neither rent nor taxes is a real arrangement, not an
        // unfinished record.
        if ((rent || space.rentCommenceDate) && !space.unitId) {
          next[key("unitId")] =
            "Assign a unit — rent is charged on the unit's area — or clear the rent.";
        }
        if (rent && !space.rentCommenceDate) {
          next[key("rentCommenceDate")] = "Rent Commence Date is required once a rent is set.";
        }
      });
    });

    if (Object.keys(next).length > 0) return { errors: next };
    return {
      errors: next,
      values: {
        operatingName: operatingName.trim(),
        legalEntity: legalEntity.trim(),
        retailerType,
        primaryContactName: contactName.trim(),
        primaryContactPhone: contactPhone.trim(),
        primaryContactEmail: contactEmail.trim(),
        wasteRemoval,
        atmRestriction,
        atmOnsite,
        depositPaidAmount: depositAmount.trim() ? parseNumeric(depositAmount) : null,
        depositPaidDate: depositDate,
        prepaidRentAmount: prepaidAmount.trim() ? parseNumeric(prepaidAmount) : null,
        prepaidRentDate: prepaidDate,
        // Which of the contact's leases this tenancy is. Saved so the question
        // is asked once, at the first review, and never again.
        smrtDocsServiceId: serviceId,
        occupancies: occupancies.map(fromOccupancyDraft),
        takeovers,
      },
    };
  };

  // Nothing to save until something has moved. Recomputed each render against
  // the snapshot taken when the form opened, so typing a value and typing it
  // back leaves Save grey — which is the honest answer.
  const dirty =
    baseline !==
    formSnapshot({
      operatingName,
      legalEntity,
      retailerType,
      contactName,
      contactPhone,
      contactEmail,
      wasteRemoval,
      atmRestriction,
      atmOnsite,
      depositAmount,
      depositDate,
      prepaidAmount,
      prepaidDate,
      serviceId,
      occupancies,
      takeovers,
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly || !onSave || busy || !dirty) return;
    const { values, errors: found } = validate();
    setErrors(found);
    if (!values) {
      // Anything wrong with the tenant's own fields reopens that section —
      // a collapsed header must never be able to swallow the reason a save
      // was refused.
      if (Object.keys(found).some((key) => !key.startsWith("occ_"))) setInfoOpen(true);

      // Open any card holding an error so the message isn't hidden behind a
      // collapsed header.
      const failed = occupancies
        .filter((o) => Object.keys(found).some((key) => key.startsWith(`occ_${o.id}_`)))
        .map((o) => o.id);
      if (failed.length) setExpanded((prev) => [...new Set([...prev, ...failed])]);
      return;
    }

    setBusy(true);
    try {
      await onSave(values);
    } finally {
      // On success the parent closes this; on failure the button has to come
      // back so the save can be retried.
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal tosp-modal-wide">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {readOnly ? "Tenant Details" : tenant ? "Edit Tenant" : "Add Tenant"}
            {tenant && (
              <span className="tosp-modal-who"> — {tenantLabel(tenant)}</span>
            )}
          </h2>
        </div>
        {/* autoComplete="off" on the form and every field: these are tenant
            records, not the user's own details, so the browser's saved-value
            dropdown is noise. */}
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          {/* Where the lease stands, above the sections rather than inside one:
              it is what most of these records are opened to check, and it
              answers before anything has to be expanded. Nothing new is stored
              or entered — every figure is read off the lease below. */}
          {tenant && <LeaseSnapshot tenant={tenant} unitById={unitById} />}

          {/* Collapsed, the header carries the tenant's name and type — enough
              to know whose record is open without reopening the section. */}
          <button
            type="button"
            className="tosp-section-toggle"
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((open) => !open)}
          >
            <span className="tosp-accordion-caret">{infoOpen ? "▾" : "▸"}</span>
            <span className="tosp-section-title">Tenant Information</span>
            {!infoOpen && (operatingName || legalEntity || retailerType) && (
              <span className="tosp-section-summary">
                {[operatingName || legalEntity, retailerType].filter(Boolean).join(" · ")}
              </span>
            )}
          </button>

          <div hidden={!infoOpen}>
          {/* The name the business trades under, and the party the lease is
              with. They're usually a suffix apart and occasionally unrelated,
              so they're recorded separately and shown side by side. */}
          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-operating-name">
                Operating Name <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-tenant-operating-name"
                className="manage-input"
                disabled={readOnly}
                autoComplete="off"
                value={operatingName}
                onChange={(e) => setOperatingName(e.target.value)}
                autoFocus
              />
              {errors.operatingName && (
                <p className="tosp-field-error">{errors.operatingName}</p>
              )}
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-legal-entity">
                Legal Entity
              </label>
              <input
                id="tosp-tenant-legal-entity"
                className="manage-input"
                disabled={readOnly}
                autoComplete="off"
                value={legalEntity}
                onChange={(e) => setLegalEntity(e.target.value)}
              />
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-retailer">
                Retailer Type <span className="tosp-required">*</span>
              </label>
              <select
                id="tosp-tenant-retailer"
                className="manage-input"
                disabled={readOnly}
                value={retailerType}
                onChange={(e) => setRetailerType(e.target.value)}
              >
                <option value="">— Select a retailer type —</option>
                {configuration.retailerTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.retailerType && <p className="tosp-field-error">{errors.retailerType}</p>}
            </div>

            {/* The other admin-maintained list, kept beside Retailer Type —
                both are configuration choices rather than things read off a
                document, and they're set together. */}
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-waste">
                Waste Removal <span className="tosp-required">*</span>
              </label>
              <select
                id="tosp-tenant-waste"
                className="manage-input"
                disabled={readOnly}
                value={wasteRemoval}
                onChange={(e) => setWasteRemoval(e.target.value)}
              >
                <option value="">— Select a waste removal type —</option>
                {configuration.wasteRemovalTypes.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {errors.wasteRemoval && <p className="tosp-field-error">{errors.wasteRemoval}</p>}
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-contact">
                Primary Contact Name <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-tenant-contact"
                className="manage-input"
                disabled={readOnly}
                autoComplete="off"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
              {errors.contactName && <p className="tosp-field-error">{errors.contactName}</p>}
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-phone">
                Primary Contact Phone <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-tenant-phone"
                className="manage-input"
                disabled={readOnly}
                autoComplete="off"
                inputMode="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
              {errors.contactPhone && <p className="tosp-field-error">{errors.contactPhone}</p>}
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-email">
                Primary Contact Email <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-tenant-email"
                className="manage-input"
                disabled={readOnly}
                autoComplete="off"
                inputMode="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
              {errors.contactEmail && <p className="tosp-field-error">{errors.contactEmail}</p>}
            </div>
          </div>

          {/* Who else the tenant has authorised. Shown, not editable: the
              tenant appoints their own delegates from their portal, and this
              is the landlord seeing who submits the figures percentage rent
              is billed on. Hidden entirely when there are none. */}
          {(tenant?.delegates || []).length > 0 && (
            <div className="tosp-form-row">
              <div className="tosp-field">
                <span className="tosp-label">Delegate Contacts</span>
                <div className="tosp-delegate-readout">
                  {(tenant?.delegates || []).map((delegate) => (
                    <div key={delegate.userId} className="tosp-delegate-readout-row">
                      <span>{delegate.name || delegate.email}</span>
                      {delegate.name && (
                        <span className="tosp-delegate-readout-email">
                          {delegate.email}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Deposit moved onto the lease. It is a term of an agreement, not a
              fact about the tenant — one company holding three leases owes three
              deposits, and the lease is where the amount can be verified. */}

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-deposit-amount">
                Security Deposit
              </label>
              <div className="tosp-affix-wrap">
                <span className="tosp-affix tosp-affix-prefix">$</span>
                <input
                  id="tosp-tenant-deposit-amount"
                  className="manage-input tosp-has-prefix"
                  disabled={readOnly}
                  autoComplete="off"
                  inputMode="decimal"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  // Tidies to money on the way out, but only when it IS a
                  // number — a half-typed or mistyped entry stays visible
                  // rather than being silently blanked.
                  onBlur={(e) => {
                    const parsed = parseNumeric(e.target.value);
                    if (parsed !== null) setDepositAmount(moneyText(parsed));
                  }}
                />
              </div>
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-deposit-date">
                Date Paid
              </label>
              <input
                id="tosp-tenant-deposit-date"
                type="date"
                className="manage-input"
                disabled={readOnly}
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
              />
            </div>
          </div>

          {/* Beneath the deposit and shaped like it: an amount the lease asks
              for up front, and the day it arrived. */}
          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-prepaid-amount">
                Prepaid Rent
              </label>
              <div className="tosp-affix-wrap">
                <span className="tosp-affix tosp-affix-prefix">$</span>
                <input
                  id="tosp-tenant-prepaid-amount"
                  className="manage-input tosp-has-prefix"
                  disabled={readOnly}
                  autoComplete="off"
                  inputMode="decimal"
                  value={prepaidAmount}
                  onChange={(e) => setPrepaidAmount(e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseNumeric(e.target.value);
                    if (parsed !== null) setPrepaidAmount(moneyText(parsed));
                  }}
                />
              </div>
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-tenant-prepaid-date">
                Date Paid
              </label>
              <input
                id="tosp-tenant-prepaid-date"
                type="date"
                className="manage-input"
                disabled={readOnly}
                value={prepaidDate}
                onChange={(e) => setPrepaidDate(e.target.value)}
              />
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-toggle-field">
              <span className="tosp-label">ATM Restriction</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={atmRestriction}
                  disabled={readOnly}
                  onChange={(e) => setAtmRestriction(e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
            <div className="tosp-toggle-field">
              <span className="tosp-label">ATM Onsite</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={atmOnsite}
                  disabled={readOnly}
                  onChange={(e) => setAtmOnsite(e.target.checked)}
                />
                <span className="slider" />
              </label>
            </div>
          </div>
          </div>

          <div className="tosp-section">
            {occupancies.length === 0 ? (
              <p className="tosp-field-hint">
                {readOnly
                  ? "No occupancies recorded."
                  : "No occupancies yet. Each one is a lease, and a lease may demise several spaces."}
              </p>
            ) : (
              occupancies.map((draft) => (
                <OccupancyCard
                  key={draft.id}
                  draft={draft}
                  expanded={expanded.includes(draft.id)}
                  onToggle={() => toggleExpanded(draft.id)}
                  onChange={(patch) => updateOccupancy(draft.id, patch)}
                  onChangeSpace={changeSpace}
                  onRemove={() =>
                    setOccupancies((prev) => prev.filter((o) => o.id !== draft.id))
                  }
                  unitOptionsFor={unitOptionsFor}
                  unitById={unitById}
                  errors={errorsFor(draft.id)}
                  readOnly={readOnly}
                  // A past tenancy has to state when it ended — the field is
                  // required by validate() and exists nowhere else on the form.
                  allowLeaseEnd={pastTenant}
                  onReviewLease={() => void startReview(draft.id)}
                  sectionTitle="Occupancy"
                />
              ))
            )}

            {errors.occupancies && <p className="tosp-field-error">{errors.occupancies}</p>}
          </div>

          {/* Reported by the tenant through their portal, shown here so the
              property manager can see what's in and what's outstanding without
              leaving the record. Nothing on this form writes to it. */}
          <div className="tosp-section">
            <button
              type="button"
              className="tosp-section-toggle"
              aria-expanded={salesOpen}
              onClick={() => setSalesOpen((open) => !open)}
            >
              <span className="tosp-accordion-caret">{salesOpen ? "▾" : "▸"}</span>
              <span className="tosp-section-title">Sales</span>
            </button>

            {salesOpen && (
              <TenantSalesPanel
                tenantId={tenant?.id || ""}
                readOnly={readOnly}
                openedOn={tenant ? tenantOpeningDate(tenant) : null}
                closedOn={tenant ? tenancyEndDate(tenant) : null}
                reportsSales={tenant ? tenantReportsSales(tenant) : true}
              />
            )}
          </div>

          {/* The same statement the tenant sees on their own Expenses screen —
              one table, so a tenant querying a figure and the property manager
              answering are looking at the same arithmetic. Derived from the
              lease, the tax rolls and Configuration; nothing here writes. */}
          <div className="tosp-section">
            <button
              type="button"
              className="tosp-section-toggle"
              aria-expanded={expensesOpen}
              onClick={() => setExpensesOpen((open) => !open)}
            >
              <span className="tosp-accordion-caret">{expensesOpen ? "▾" : "▸"}</span>
              <span className="tosp-section-title">Expenses</span>
            </button>

            {expensesOpen && tenant && <ExpenseTable tenant={tenant} sales={sales} audience="admin" />}
            {expensesOpen && !tenant && (
              <p className="tosp-field-hint">Save the tenant before a statement can be built.</p>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: readOnly ? "flex-end" : "space-between",
            }}
          >
            {/* Asks only when there is something to lose. A lease review is the
                reason this exists: verifying eighty pages fills the form but
                writes nothing until Save, so Cancel at that moment throws away
                a long piece of careful work with no warning. `dirty` is the
                right gate — a form opened and closed untouched must not nag. */}
            <button
              type="button"
              className="btn-cancel"
              disabled={busy}
              onClick={() => (!readOnly && dirty ? setConfirmDiscard(true) : onClose())}
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="btn-save"
                disabled={busy || !dirty}
                title={dirty ? undefined : "Nothing has changed"}
              >
                {busy ? (
                  <>
                    <span className="tosp-spinner" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {takeoverWizard}

      {/* Named plainly. "Are you sure?" doesn't tell anyone what they are about
          to lose, and a lease review is the case where the answer matters. */}
      <DeleteConfirmModal
        isOpen={confirmDiscard}
        title="Discard changes?"
        message={
          reviewedThisSession
            ? "This lease has been reviewed but not saved. Closing now discards the review and every other change on this form."
            : "Changes on this form haven't been saved. Closing now discards them."
        }
        confirmLabel="Discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
      />
    </div>
  );
}
